import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import MessageToastComponent from '../../components/MessageToastComponent/MessageToastComponent';
import ErrorText from '../../components/ErrorText/ErrorText';
import { getRequestedLanguages } from '../../locale/languages';
import { BROCHURES_LIST_PATH } from '../../utils/route.constants';
import {
    getBrochureBasketById,
    deleteBrochureBasket,
    deleteProductFromBrochureBasket,
    deleteCustomProductFromBrochureBasket,
    BrochureBasketDetail,
    BrochureProduct,
    CustomProduct,
    getBrochureById,
} from '../../api/brochures';
import { Route } from './types';
import ZakekeViewer from '../../components/ZakekeViewer/ZakekeViewer';
import BrochureProductRow from './BrochureProductRow';
import BrochureConfirmModal from '../../components/BrochureConfirmModal/BrochureConfirmModal';
import { format } from 'date-fns';

interface Props {
    basketId: string;
    data: BrochureBasketDetail | null;
    onDataChange: (data: BrochureBasketDetail | null) => void;
    onLoadingChange: (loading: boolean) => void;
    onNavigate: (route: Route) => void;
}

interface ZakekeViewerState {
    product: BrochureProduct;
    designId?: string;
}

type PendingConfirm =
    | 'basket'
    | { type: 'product'; productId: number }
    | { type: 'custom-product'; productId: number; customProductId: number };

const BrochureDetailView: FC<Props> = ({ basketId, data, onDataChange, onLoadingChange, onNavigate }) => {
    const language = getRequestedLanguages(window.errea_accountUserData.language);
    const lang = (window.errea_accountUserData.language || 'it').split('-')[0].toLowerCase();

    const [error, setError] = useState('');
    const [zakekeError, setZakekeError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deletingRowIds, setDeletingRowIds] = useState<Set<string>>(new Set());
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
    const [zakekeProduct, setZakekeProduct] = useState<ZakekeViewerState | null>(null);

    const handleConfigureProduct = (product: BrochureProduct) => {
        setZakekeProduct({ product });
    };

    const handleEditProduct = (product: BrochureProduct, customProduct: CustomProduct) => {
        setZakekeProduct({ product, designId: customProduct.zakeke_design_id });
    };

    const loadData = useCallback(() => {
        if (!basketId) return;
        onLoadingChange(true);
        getBrochureBasketById(basketId, lang)
            .then((result: BrochureBasketDetail) => {
                onDataChange(result);
            })
            .catch(() => {
                setError(language.brochures.detail.error_loading);
            })
            .finally(() => {
                onLoadingChange(false);
            });
    }, [basketId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleHideError = useCallback(() => setError(''), []);

    const isProductInBrochure = async (product: BrochureProduct | CustomProduct) => {
        if (!data) return false;

        const brochures = await Promise.all(
            data.brochures.map(({ id }) => getBrochureById(data.id.toString(), id.toString())),
        );

        const isInBrochure = brochures.some((brochure) => {
            const isProductInPage = brochure.pages.some((page) => {
                if ('product_id' in product) {
                    const hasCustomProduct = page.products.some((p) => p.custom_product?.id === product.id);
                    return hasCustomProduct;
                }

                const hasProduct = page.products.some((p) => p.id === product.id);
                return hasProduct;
            });

            return isProductInPage;
        });

        return isInBrochure;
    };

    const handleDeleteProduct = async (product: BrochureProduct) => {
        const canDeleteProduct = !(await isProductInBrochure(product));
        if (!canDeleteProduct) {
            setError(language.brochures.detail.error_product_in_brochure);
            return;
        }

        setPendingConfirm({ type: 'product', productId: product.id });
    };

    const handleDeleteCustomProduct = async (product: BrochureProduct, customProduct: CustomProduct) => {
        const canDeleteProduct = !(await isProductInBrochure(customProduct));
        if (!canDeleteProduct) {
            setError(language.brochures.detail.error_product_in_brochure);
            return;
        }

        setPendingConfirm({ type: 'custom-product', productId: product.id, customProductId: customProduct.id });
    };

    const handleConfirm = () => {
        if (pendingConfirm === null) return;

        if (pendingConfirm === 'basket') {
            setPendingConfirm(null);
            setDeleting(true);
            deleteBrochureBasket(basketId)
                .then(() => {
                    window.location.href = `${window?.Shopify?.routes?.root ?? '/'}${BROCHURES_LIST_PATH}`;
                })
                .catch(() => {
                    setError(language.brochures.detail.error_deleting_collection);
                    setDeleting(false);
                });
            return;
        }

        if (pendingConfirm.type === 'product') {
            const { productId } = pendingConfirm;
            const rowId = `product-${productId}`;
            setPendingConfirm(null);
            setDeletingRowIds((prev) => new Set(prev).add(rowId));
            deleteProductFromBrochureBasket(basketId, productId)
                .then(() => {
                    onDataChange(data ? { ...data, products: data.products.filter((p) => p.id !== productId) } : data);
                })
                .catch(() => {
                    setError(language.brochures.detail.error_deleting_product);
                })
                .finally(() => {
                    setDeletingRowIds((prev) => {
                        const next = new Set(prev);
                        next.delete(rowId);
                        return next;
                    });
                });
            return;
        }

        const { productId, customProductId } = pendingConfirm;
        const rowId = `custom-${customProductId}`;
        setPendingConfirm(null);
        setDeletingRowIds((prev) => new Set(prev).add(rowId));
        deleteCustomProductFromBrochureBasket(basketId, productId, customProductId)
            .then(() => {
                onDataChange(
                    data
                        ? {
                              ...data,
                              products: data.products.map((product) =>
                                  product.id !== productId
                                      ? product
                                      : {
                                            ...product,
                                            custom_products: product.custom_products?.filter(
                                                (customProduct) => customProduct.id !== customProductId,
                                            ),
                                        },
                              ),
                          }
                        : data,
                );
            })
            .catch(() => {
                setError(language.brochures.detail.error_deleting_custom_product);
            })
            .finally(() => {
                setDeletingRowIds((prev) => {
                    const next = new Set(prev);
                    next.delete(rowId);
                    return next;
                });
            });
    };

    if (!data) {
        return (
            <div className="brochureDetailPage">
                <ErrorText message={error} />
            </div>
        );
    }

    return (
        <div className="brochureDetailPage" onClick={() => pendingConfirm !== null && setPendingConfirm(null)}>
            <MessageToastComponent message={error} type="error" onHide={handleHideError} />
            <ErrorText message={zakekeError} />
            {zakekeProduct && (
                <ZakekeViewer
                    product={zakekeProduct.product}
                    designId={zakekeProduct.designId}
                    brochureBasketId={basketId}
                    onClose={() => {
                        setZakekeProduct(null);
                        loadData();
                    }}
                    onError={(msg) => setZakekeError(`Zakeke: ${msg}`)}
                />
            )}
            <a href={`${window?.Shopify?.routes?.root ?? '/'}${BROCHURES_LIST_PATH}`} className="brochureDetailBack">
                <i className="icon errea-arrow-left"></i>
                <span>{language.brochures.detail.go_back}</span>
            </a>

            <div className="brochureDetailTitleRow">
                <div className="brochureDetailTitleBlock">
                    <h1 className="brochureDetailTitle">
                        <span className="brochureDetailTitleSeason">{data.name}</span>
                        {' - '}
                        <span className="brochureDetailTitleCompany">{data.company}</span>
                    </h1>
                    {/* TODO: Confirm update date removal */}
                    {/* {data.updated_at && (
                        <span className="brochureDetailUpdatedAt">
                            {language.brochures.detail.last_modified}: {new Date(data.updated_at).toLocaleDateString()}
                        </span>
                    )} */}
                </div>
                <div
                    className={`brochureDetailDeleteAction${deleting ? ' brochureDetailDeleteAction--disabled' : ''}`}
                    onClick={!deleting ? () => setPendingConfirm('basket') : undefined}
                >
                    <span>{language.brochures.detail.delete_collection}</span>
                    <i className="icon errea-trash"></i>
                </div>
            </div>

            <div className="brochureDetailContent">
                <div className="brochureDetailLeftPanel">
                    <h2 className="brochureDetailPanelTitle">
                        {language.brochures.detail.collection_title} ({data.products.length})
                    </h2>
                    <div className="brochureDetailProductsList">
                        {data.products.map((product) => (
                            <Fragment key={product.id}>
                                <BrochureProductRow
                                    product={product}
                                    isDeleting={deletingRowIds.has(`product-${product.id}`)}
                                    onConfigure={() => handleConfigureProduct(product)}
                                    onDelete={() => handleDeleteProduct(product)}
                                />
                                {product.custom_products?.map((customProduct) => (
                                    <BrochureProductRow
                                        key={`${product.id}-custom-${customProduct.id}`}
                                        product={product}
                                        customProduct={customProduct}
                                        isDeleting={deletingRowIds.has(`custom-${customProduct.id}`)}
                                        onEdit={() => handleEditProduct(product, customProduct)}
                                        onDelete={() => handleDeleteCustomProduct(product, customProduct)}
                                    />
                                ))}
                            </Fragment>
                        ))}
                    </div>
                </div>

                <div className="brochureDetailRightPanel">
                    {data.brochures.length > 0 && (
                        <>
                            <h2 className="brochureDetailPanelTitle">
                                {language.brochures.detail.brochure} ({data.brochures.length})
                            </h2>
                            <div className="brochureDetailBrochuresList">
                                {data.brochures.map((brochure) => (
                                    <div
                                        key={brochure.id}
                                        className="brochureDetailBrochureCard"
                                        onClick={() => onNavigate({ view: 'configurator', id: String(brochure.id) })}
                                    >
                                        <span className="brochureDetailBrochureName">{brochure.name}</span>
                                        {(brochure.updated_at || brochure.last_modified) && (
                                            <span className="brochureDetailBrochureMeta">
                                                {language.brochures.detail.last_modified}:{' '}
                                                {brochure.updated_at
                                                    ? format(
                                                          new Date(brochure.updated_at),
                                                          language.general.date_format,
                                                      )
                                                    : format(
                                                          new Date(brochure.last_modified ?? ''),
                                                          language.general.date_format,
                                                      )}
                                            </span>
                                        )}
                                        {brochure.products_count !== undefined && (
                                            <span className="brochureDetailBrochureMeta">
                                                {language.brochures.detail.products_count}: {brochure.products_count}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    <div className="brochureDetailCreateBtn">
                        <ButtonComponent
                            style="solid"
                            text={language.brochures.detail.create_brochure}
                            icon_left="add"
                            onClick={() => onNavigate({ view: 'configurator', id: 'new' })}
                        />
                    </div>
                </div>
            </div>
            {pendingConfirm === 'basket' && (
                <BrochureConfirmModal
                    text={language.brochures.detail.confirm_delete_collection}
                    onConfirm={handleConfirm}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
            {pendingConfirm !== 'basket' && pendingConfirm?.type === 'product' && (
                <BrochureConfirmModal
                    text={language.brochures.detail.confirm_delete_product}
                    onConfirm={handleConfirm}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
            {pendingConfirm !== 'basket' && pendingConfirm?.type === 'custom-product' && (
                <BrochureConfirmModal
                    text={language.brochures.detail.confirm_delete_custom_product}
                    onConfirm={handleConfirm}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
        </div>
    );
};

export default BrochureDetailView;
